import React, { Component } from "react";
import Hls from "hls.js";
import { imageBaseUrl } from "../../../redux/apis/api";
import "./VideoPlayer.css";

class VideoPlayer extends Component {
  constructor(props) {
    super(props);

    this.videoRef = React.createRef();
    this.containerRef = React.createRef();
    this.hls = null;

    this.state = {
      playing: false,
      muted: false,
      volume: 1,
      currentTime: 0,
      duration: 0,
      buffered: 0,
      seeking: false,
      showControls: false,
    };
  }

  convertToCloudFront = (url) => {
  try {
    const parsed = new URL(url);

    if (parsed.hostname === "node6.apigate.pro") {
      return "https://d35pld11nuhae4.cloudfront.net" + parsed.pathname;
    }

    return url;
  } catch (e) {
    console.error("Invalid URL:", url);
    return url;
  }
};

  componentDidMount() {
    this.loadVideo();
    this.attachVideoListeners();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.loadVideo();
      this.setState({
        playing: false,
        currentTime: 0,
        duration: 0,
        buffered: 0,
      });
    }
  }

  componentWillUnmount() {
    if (this.hls) {
      this.hls.destroy();
    }
    this.removeVideoListeners();
  }

  /* ---------------- VIDEO LOAD ---------------- */

 loadVideo = () => {
  const video = this.videoRef.current;
  let { src } = this.props;

    const originalSrc = src;
    src = this.convertToCloudFront(originalSrc);


  if (!video || !src) return;

  if (this.hls) {
    this.hls.destroy();
    this.hls = null;
  }

  if (src.includes(".m3u8")) {

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
    } 
    else if (Hls.isSupported()) {

      this.hls = new Hls();

      this.hls.loadSource(src);
      this.hls.attachMedia(video);

      this.hls.on(Hls.Events.ERROR, (event, data) => {
        console.log("HLS ERROR:", data);
      });

    }

  } 
  else {

    video.src = src;
    video.load();

  }
};

  /* ---------------- VIDEO EVENTS ---------------- */

  attachVideoListeners = () => {
    const video = this.videoRef.current;
    if (!video) return;

    this.handleTimeUpdate = () => {
      if (!this.state.seeking) {
        this.setState({ currentTime: video.currentTime });
      }
    };

    this.handleLoadedMetadata = () => {
      this.setState({ duration: video.duration });
    };

    this.handleEnded = () => {
      this.setState({ playing: false });
    };

    this.handleProgress = () => {
      if (video.buffered.length > 0) {
        this.setState({
          buffered: video.buffered.end(video.buffered.length - 1),
        });
      }
    };

    video.addEventListener("timeupdate", this.handleTimeUpdate);
    video.addEventListener("loadedmetadata", this.handleLoadedMetadata);
    video.addEventListener("ended", this.handleEnded);
    video.addEventListener("progress", this.handleProgress);
  };

  removeVideoListeners = () => {
    const video = this.videoRef.current;
    if (!video) return;

    video.removeEventListener("timeupdate", this.handleTimeUpdate);
    video.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    video.removeEventListener("ended", this.handleEnded);
    video.removeEventListener("progress", this.handleProgress);
  };

  /* ---------------- CONTROLS ---------------- */

  togglePlay = () => {
    const video = this.videoRef.current;

    if (!video) return;

    if (this.state.playing) {
      video.pause();
      this.setState({ playing: false });
    } else {
      video.play();
      this.setState({ playing: true });
    }
  };

  toggleMute = () => {
    const video = this.videoRef.current;
    video.muted = !video.muted;

    this.setState({ muted: video.muted });
  };

  handleVolumeChange = (e) => {
    const video = this.videoRef.current;

    const volume = parseFloat(e.target.value);

    video.volume = volume;

    this.setState({
      volume,
      muted: volume === 0,
    });
  };

  handleSeek = (e) => {
    const video = this.videoRef.current;

    const newTime = (e.target.value / 100) * this.state.duration;

    video.currentTime = newTime;

    this.setState({ currentTime: newTime });
  };

  toggleFullscreen = () => {
    const container = this.containerRef.current;

    if (!document.fullscreenElement) {
      container.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  formatTime = (time) => {
    if (isNaN(time)) return "0:00";

    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);

    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  /* ---------------- RENDER ---------------- */

  render() {
    const { poster } = this.props;
    const { playing, muted, volume, currentTime, duration, buffered, showControls } = this.state;

    const progress = duration ? (currentTime / duration) * 100 : 0;
    const bufferedPercent = duration ? (buffered / duration) * 100 : 0;

    return (
      <div
        className="video-player-container"
        ref={this.containerRef}
        onMouseEnter={() => this.setState({ showControls: true })}
        onMouseLeave={() => this.setState({ showControls: false })}
      >

        <video
          ref={this.videoRef}
          poster={imageBaseUrl + poster}
          className="video-element"
          playsInline
          preload="metadata"
          onClick={this.togglePlay}
        />

        {showControls && (
          <div className="video-controls">

            <div className="progress-bar-container">

              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={this.handleSeek}
              />

              <div
                className="buffered-progress"
                style={{ width: `${bufferedPercent}%` }}
              />

            </div>

            <div className="controls-row">

              <button onClick={this.togglePlay}>
                {playing ? "⏸" : "▶"}
              </button>

              <button onClick={this.toggleMute}>
                {muted ? "🔇" : "🔊"}
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={this.handleVolumeChange}
              />

              <span>
                {this.formatTime(currentTime)} / {this.formatTime(duration)}
              </span>

              <button onClick={this.toggleFullscreen}>
                ⛶
              </button>

            </div>

          </div>
        )}

      </div>
    );
  }
}

export default VideoPlayer;